import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmDialog } from '../../components/alianzas/ui';
import { weeklyReportApi as api, apiErrorMessage } from '../../services/weeklyReportApi';
import { TABLES, buildReport, formPayload, normalize, today } from './weeklyReport';
import './ReporteSemanalPage.css';

const money = (value) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'USD' }).format(value);
const dateLabel = (value) => value ? value.split('-').reverse().join('/') : 'Sin dato';
const absent = (value) => value === null || value === undefined || value === '';
const errorText = (err) => apiErrorMessage(err, 'No se pudo completar la operación.');

function WeeklyDialog({ title, busy, onClose, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    ref.current.querySelector('input, select, textarea, button')?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, []);
  const keyboard = (event) => {
    if (event.key === 'Escape' && !busy) onClose();
    if (event.key !== 'Tab') return;
    const controls = [...ref.current.querySelectorAll('input:not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled)')];
    if (!controls.length) { event.preventDefault(); ref.current.focus(); return; }
    if (event.shiftKey && (document.activeElement === controls[0] || !controls.includes(document.activeElement))) {
      event.preventDefault(); controls[controls.length - 1].focus();
    } else if (!event.shiftKey && (document.activeElement === controls[controls.length - 1] || !controls.includes(document.activeElement))) {
      event.preventDefault(); controls[0].focus();
    }
  };
  return <div ref={ref} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} onKeyDown={keyboard}>{children}</div>;
}

function Cell({ value, column }) {
  if (absent(value)) return <span className="weekly-muted">Sin dato</span>;
  if (column === '% Avance planeado') return <span className="weekly-progress"><progress max="1" value={value} aria-label="Avance planeado" />{Math.round(value * 100)}%</span>;
  if (column.includes('semáforo')) return <span className={`weekly-status weekly-${normalize(value)}`}>{value}</span>;
  if (column === 'Fecha' || column.startsWith('Fecha ')) return dateLabel(value);
  if (/USD\/mes/.test(column)) return money(value);
  return String(value);
}

function ReportTable({ sheet, actions }) {
  const [page, setPage] = useState(0);
  const size = 25, pages = Math.ceil(sheet.rows.length / size);
  if (!sheet.rows.length) return <p className="weekly-empty">Sin registros para este alcance.</p>;
  return <>
    <div className="weekly-table-wrap"><table>
      <thead><tr>{sheet.headers.map((h) => <th key={h} scope="col">{h}</th>)}{actions && <th scope="col">Acciones</th>}</tr></thead>
      <tbody>{sheet.rows.slice(page * size, (page + 1) * size).map((row) => <tr key={row.id}>
        {sheet.headers.map((h) => <td key={h}><Cell value={row[h]} column={h} /></td>)}
        {actions && <td>{actions(row.id)}</td>}
      </tr>)}</tbody>
    </table></div>
    {pages > 1 && <div className="weekly-pagination">
      <button type="button" disabled={!page} onClick={() => setPage(page - 1)}>Anterior</button>
      <span>Página {page + 1} de {pages} · {sheet.rows.length} registros</span>
      <button type="button" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>Siguiente</button>
    </div>}
  </>;
}

function StatusSummary({ statuses, label }) {
  return <div>
    <div className="weekly-statusbar" role="img" aria-label={`${label}: ${Object.entries(statuses).map(([name, count]) => `${count} ${name}`).join(', ')}`}>
      {Object.entries(statuses).filter(([, n]) => n > 0).map(([name, n]) =>
        <span key={name} className={`weekly-${name.replace(' ', '-')}`} style={{ flex: n }} />)}
    </div>
    <div className="weekly-legend">{Object.entries(statuses).map(([name, count]) =>
      <span key={name}>{name}: <strong>{count}</strong></span>)}</div>
  </div>;
}

function Dashboard({ report }) {
  return <>
    <section><h3>1. Resumen</h3><div className="weekly-tiles">
      <div><span>Clientes SA en verde</span><strong>{report.adminStatuses.verde} / {report.clientCount}</strong><small>{report.sla} tickets fuera de SLA · {report.incidents} incidentes mayores · {report.slaMissing} filas con conteos incompletos</small></div>
      <div><span>Proyectos SP</span><strong>{report.projects}</strong><small>{report.escalations} requieren escalamiento</small></div>
      <div><span>Pipeline abierto</span><strong>{money(report.pipelineAmount)}</strong><small>USD/mes · {report.pipeline.length} oportunidades · {report.pipelineMissing} sin monto</small></div>
      <div><span>Temas para dirección</span><strong>{report.issues.length}</strong><small>{report.issues.filter((r) => normalize(r.Impacto) === 'alto').length} de impacto alto</small></div>
    </div></section>
    <section><h3>2. Servicios Administrados</h3>
      <StatusSummary statuses={report.adminStatuses} label="Semáforo SA" /><ReportTable sheet={report.sections.servicios} />
    </section>
    <section><h3>3. Servicios Profesionales</h3>
      <StatusSummary statuses={report.projectStatuses} label="Semáforo SP" />
      <p>Presupuesto informado: {new Intl.NumberFormat('es-MX').format(report.budget)} (moneda no especificada) · {report.budgetMissing} proyectos sin presupuesto.</p>
      <ReportTable sheet={report.sections.proyectos} />
    </section>
    <section><h3>4. Pipeline</h3>
      <p>Total abierto: {money(report.pipelineAmount)} USD/mes. Excluye Ganada y Perdida; {report.pipelineMissing} oportunidades sin monto.</p>
      <div className="weekly-bars">{report.pipelineGroups.map(({ name, amount }) =>
        <div key={name}><span>{name}</span><progress max={Math.max(report.pipelineAmount, 1)} value={amount} aria-label={name} /><strong>{money(amount)}</strong></div>)}</div>
      <ReportTable sheet={{ ...report.sections.pipeline, rows: report.pipeline }} />
      <details><summary>Todas las oportunidades, incluidas ganadas y perdidas</summary><ReportTable sheet={report.sections.pipeline} /></details>
    </section>
    <section><h3>5. Temas para dirección</h3><p>Marcados para dirección y no cerrados.</p>
      <ReportTable sheet={{ ...report.sections.temas, rows: report.issues }} />
      <details><summary>Todos los temas</summary><ReportTable sheet={report.sections.temas} /></details>
    </section>
  </>;
}

function RecordForm({ kind, record, onClose, onSave }) {
  const table = TABLES[kind];
  const [form, setForm] = useState(() => Object.fromEntries(table.fields.map((f) => [f.key,
    record ? (f.percent && record.datos[f.key] != null ? record.datos[f.key] * 100 : record.datos[f.key] ?? '')
      : f.type === 'checkbox' ? false : f.key === 'fecha' ? today() : '',
  ])));
  const [saving, setSaving] = useState(false), [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true); setError('');
    try { await onSave(formPayload(kind, form)); } catch (err) { setError(errorText(err)); } finally { setSaving(false); }
  };
  return <WeeklyDialog title={`${record ? 'Editar' : 'Nuevo registro'} · ${table.label}`} busy={saving} onClose={onClose}><div className="weekly-modal-backdrop">
    <section className="weekly-modal">
      <h2 id="weekly-form-title">{record ? 'Editar' : 'Nuevo registro'} · {table.label}</h2>
      {error && <p role="alert" className="weekly-error">{error}</p>}
      <form onSubmit={submit}>
        <fieldset disabled={saving} className="weekly-form-grid">
          {table.fields.map((f) => <label key={f.key} htmlFor={`weekly-${f.key}`}>{f.label}{f.required ? ' *' : ''}
            {f.type === 'select' ? <select id={`weekly-${f.key}`} required={f.required} value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>
              <option value="">Selecciona</option>{f.options.map((o) => <option key={o}>{o}</option>)}
            </select> : f.type === 'textarea' ? <textarea id={`weekly-${f.key}`} required={f.required} maxLength={4000} value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
              : <input id={`weekly-${f.key}`} type={f.type} required={f.required}
                min={f.type === 'number' ? 0 : undefined} max={f.max} maxLength={f.type === 'text' ? 200 : undefined} step={f.step || 1}
                {...(f.type === 'checkbox' ? { checked: Boolean(form[f.key]) } : { value: form[f.key] })}
                onChange={(e) => setForm({ ...form, [f.key]: f.type === 'checkbox' ? e.target.checked : e.target.value })} />}
          </label>)}
        </fieldset>
        <div className="weekly-controls"><button disabled={saving} type="submit">{saving ? 'Guardando…' : 'Guardar'}</button>
          <button type="button" disabled={saving} onClick={onClose}>Cancelar</button></div>
      </form>
    </section>
  </div></WeeklyDialog>;
}

export default function ReporteSemanalPage() {
  const [draft, setDraft] = useState(null), [cuts, setCuts] = useState([]);
  const [view, setView] = useState('dashboard'), [kind, setKind] = useState('servicios');
  const [selected, setSelected] = useState('draft'), [cut, setCut] = useState(null);
  const [loading, setLoading] = useState(true), [cutLoading, setCutLoading] = useState(false);
  const [error, setError] = useState(''), [notice, setNotice] = useState(''), [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null), [confirmation, setConfirmation] = useState(null);
  const [filters, setFilters] = useState({ cliente: '', nube: '', responsable: '' });
  const [search, setSearch] = useState(''), [archived, setArchived] = useState(false);
  const [cutDate, setCutDate] = useState(today), [title, setTitle] = useState('');
  const [cutsOffset, setCutsOffset] = useState(0), [cutsMore, setCutsMore] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [nextDraft, nextCuts] = await Promise.all([api.draft(), api.cuts()]);
      if (!alive.current) return;
      setDraft(nextDraft); setCuts(nextCuts.data); setCutsOffset(0); setCutsMore(nextCuts.data.length === 50);
      setRefreshVersion((version) => version + 1);
    } catch (err) { if (alive.current) { setDraft(null); setError(errorText(err)); } }
    finally { if (alive.current) setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    let active = true;
    setCut(null);
    if (selected === 'draft') { setCutLoading(false); return undefined; }
    setCutLoading(true); setError('');
    api.cut(selected).then((value) => { if (active) setCut(value); })
      .catch((err) => { if (active) setError(errorText(err)); })
      .finally(() => { if (active) setCutLoading(false); });
    return () => { active = false; };
  }, [selected, refreshVersion]);
  const content = selected === 'draft' ? draft?.contenido : cut?.contenido;
  const report = useMemo(() => content ? buildReport(content, filters) : null, [content, filters]);
  const records = draft?.contenido[kind] || [];
  const shown = records.filter((r) => r.activo !== archived && Object.values(r.datos).some((v) => normalize(v).includes(normalize(search))));
  const management = buildReport({ [kind]: shown.map((r) => ({ ...r, activo: true })) }).sections[kind];
  const mutate = async (operation, success) => {
    setBusy(true); setError(''); setNotice('');
    try { await operation(); setNotice(success); setConfirmation(null); await refresh(); }
    catch (err) { setError(errorText(err)); }
    finally { setBusy(false); }
  };
  const save = async (data) => {
    if (modal.record) await api.update(kind, modal.record.id, data, modal.revision);
    else await api.create(kind, data, modal.revision);
    setModal(null); setNotice('Registro guardado. Los cortes publicados no cambian.'); await refresh();
  };
  const loadMore = async () => {
    setBusy(true); setError('');
    try {
      const result = await api.cuts(cutsOffset + 50);
      setCuts((current) => [...current, ...result.data]); setCutsOffset(cutsOffset + 50); setCutsMore(result.data.length === 50);
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  };
  return <main className="weekly-report">
    <header className="weekly-heading"><p className="weekly-eyebrow">Servicios Profesionales</p><h1>Reporte Semanal Cloud</h1>
      <p>Captura y administra tus tablas. Publica cortes semanales que no cambian al editar el borrador.</p></header>
    <div className="weekly-controls">
      <button type="button" aria-pressed={view === 'dashboard'} onClick={() => setView('dashboard')}>Reporte / Dashboard</button>
      <button type="button" aria-pressed={view === 'gestion'} onClick={() => setView('gestion')}>Gestión</button>
      <button type="button" disabled={busy || loading || Boolean(modal) || Boolean(confirmation)} onClick={refresh}>Recargar datos</button>
    </div>
    {error && <p role="alert" className="weekly-error">{error} Usa «Recargar datos» antes de reintentar si hubo un conflicto.</p>}
    {notice && <p role="status">{notice}</p>}
    {loading && <p role="status">Cargando tablas…</p>}
    {!loading && draft && (view === 'dashboard' ? <>
      <div className="weekly-controls">
        <label>Fuente del reporte<select value={selected} onChange={(e) => setSelected(e.target.value)}>
          <option value="draft">Borrador actual (sin publicar)</option>
          {cuts.map((c) => <option key={c.id} value={c.id}>{c.fecha_corte} · {c.titulo}</option>)}
        </select></label>
        {cutsMore && <button type="button" disabled={busy} onClick={loadMore}>Cargar cortes anteriores</button>}
        <label>Cliente<input value={filters.cliente} onChange={(e) => setFilters({ ...filters, cliente: e.target.value })} /></label>
        <label>Nube<select value={filters.nube} onChange={(e) => setFilters({ ...filters, nube: e.target.value })}>
          <option value="">Todas</option>{['AWS', 'Azure', 'GCP', 'Huawei', 'OCI', 'Multicloud'].map((c) => <option key={c}>{c}</option>)}
        </select></label>
        <label>Responsable / Preventa<input value={filters.responsable} onChange={(e) => setFilters({ ...filters, responsable: e.target.value })} /></label>
        <button type="button" onClick={() => setFilters({ cliente: '', nube: '', responsable: '' })}>Limpiar filtros</button>
      </div>
      {selected === 'draft' && <form className="weekly-controls" onSubmit={(e) => {
        e.preventDefault();
        setConfirmation({ publish: true, revision: draft.revision, titulo: title.trim(), fecha_corte: cutDate });
      }}>
        <label>Título del corte<input value={title} required maxLength={200} onChange={(e) => setTitle(e.target.value)} /></label>
        <label>Fecha de corte<input type="date" required max={today()} value={cutDate} onChange={(e) => setCutDate(e.target.value)} /></label>
        <button disabled={busy || !Object.values(draft.contenido).some((rows) => rows.some((r) => r.activo))}>Publicar corte semanal</button>
        <p>Se publica todo el borrador activo, no solo los datos filtrados. Un corte por fecha; no admite cambios ni eliminación.</p>
      </form>}
      {cutLoading && <p role="status">Cargando corte…</p>}
      {!cutLoading && report && <article className="weekly-document" key={`${selected}-${draft.revision}-${JSON.stringify(filters)}`}>
        <header><p className="weekly-eyebrow">KIO · Nube Pública · Uso interno</p><h2>{cut?.titulo || 'Reporte Semanal Nube Pública'}</h2>
          <p>{selected === 'draft' ? `Borrador editable · revisión ${draft.revision}` : `Corte publicado e inmutable al ${dateLabel(cut.fecha_corte)} · ${cut.publicado_por}`}</p></header>
        <details className="weekly-method"><summary>Alcance y criterios</summary>
          <p>El borrador muestra todos los registros activos. Cada corte conserva una copia completa al publicar; los cambios y archivos posteriores no modifican esa copia. Los filtros solo afectan la visualización.</p>
          <p>Identificadores únicos distinguen proyectos y oportunidades del mismo cliente. SA toma el peor semáforo por cliente. Montos vacíos no son cero. El presupuesto no especifica moneda y no se suma al pipeline USD/mes. No se usa IA.</p>
        </details><Dashboard report={report} />
      </article>}
    </> : <>
      <div className="weekly-controls">
        <label>Tabla<select value={kind} onChange={(e) => { setKind(e.target.value); setSearch(''); }}>{Object.entries(TABLES).map(([key, t]) => <option key={key} value={key}>{t.label}</option>)}</select></label>
        <label>Buscar<input value={search} onChange={(e) => setSearch(e.target.value)} /></label>
        <label><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />Ver archivados</label>
        <button type="button" disabled={busy} onClick={() => setModal({ record: null, revision: draft.revision })}>Nuevo registro</button>
      </div>
      <p>Revisión {draft.revision} · Última edición: {draft.editado_por || 'Sin cambios'} · {draft.editado_en || 'Sin fecha'}. Archivar no borra el historial publicado.</p>
      <article className="weekly-document"><h2>{TABLES[kind].label}</h2>
        <ReportTable key={`${kind}-${search}-${archived}-${draft.revision}`} sheet={management} actions={(id) => {
          const record = records.find((r) => r.id === id);
          return <div className="weekly-actions">
            {record.activo && <button type="button" disabled={busy} onClick={() => setModal({ record, revision: draft.revision })}>Editar</button>}
            <button type="button" disabled={busy} onClick={() => setConfirmation({ record, kind, revision: draft.revision })}>{record.activo ? 'Archivar' : 'Restaurar'}</button>
            <small>Actualizado por {record.editado_por} · {record.editado_en}</small>
          </div>;
        }} />
      </article>
    </>)}
    {modal && <RecordForm key={`${kind}-${modal.record?.id || 'new'}`} kind={kind} record={modal.record} onSave={save} onClose={() => setModal(null)} />}
    {confirmation && <WeeklyDialog title="Confirmar operación" busy={busy} onClose={() => setConfirmation(null)}><ConfirmDialog title={confirmation.publish ? 'Publicar corte inmutable' : confirmation.record.activo ? 'Archivar registro' : 'Restaurar registro'}
      message={confirmation.publish ? `Se publicará todo el borrador al ${dateLabel(confirmation.fecha_corte)} con el título «${confirmation.titulo}». No podrá editarse ni eliminarse.`
        : 'El cambio solo afecta al borrador; los cortes publicados se conservan.'}
      confirmLabel={confirmation.publish ? 'Confirmar publicación' : 'Confirmar'} busy={busy} onClose={() => { if (!busy) setConfirmation(null); }}
      onConfirm={() => mutate(async () => {
        if (confirmation.publish) {
          const result = await api.publish({ titulo: confirmation.titulo, fecha_corte: confirmation.fecha_corte, revision: confirmation.revision });
          setSelected(result.id); setView('dashboard');
        } else await api.archive(confirmation.kind, confirmation.record.id, confirmation.revision, !confirmation.record.activo);
      }, confirmation.publish ? 'Corte publicado. Su contenido ya no puede modificarse.' : 'Registro actualizado.')} /></WeeklyDialog>}
  </main>;
}
